import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";

import { deleteShift, listPayouts, markWorkerPaid } from "@/features/ops/api/opsApi";
import { useOpsStore } from "@/features/ops/model/opsStore";
import type { CompletedShiftRow } from "@/features/ops/types";
import { ROLE_LABELS } from "@/features/shifts/types";
import { cn } from "@/shared/lib/cn";
import { formatTimeRange12h } from "@/shared/lib/time";
import { Badge, Button, ConfirmDialog } from "@/shared/ui";

const TABS: Array<{ label: string; value: "unpaid" | "paid" | "all" }> = [
  { label: "Due", value: "unpaid" },
  { label: "Paid", value: "paid" },
  { label: "All", value: "all" },
];

export function WorkerPayouts() {
  const token = useOpsStore((s) => s.token)!;
  const qc = useQueryClient();
  const [tab, setTab] = useState<"unpaid" | "paid" | "all">("unpaid");

  const paidParam = tab === "unpaid" ? false : tab === "paid" ? true : undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: ["ops", "payouts", tab],
    queryFn: () => listPayouts(token, paidParam),
    // Workers checking out in other sessions land here as new rows — poll
    // so the unpaid queue stays current.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState<CompletedShiftRow | null>(null);

  const remove = useMutation({
    mutationFn: (shiftId: string) => deleteShift(token, shiftId),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ["ops", "payouts"] });
      qc.invalidateQueries({ queryKey: ["ops", "upcoming-shifts"] });
      qc.invalidateQueries({ queryKey: ["ops", "invoices"] });
      qc.invalidateQueries({ queryKey: ["earnings"] });
      qc.invalidateQueries({ queryKey: ["timesheets"] });
    },
  });

  const pay = useMutation({
    mutationFn: (shiftId: string) => markWorkerPaid(token, shiftId),
    onMutate: async (shiftId) => {
      setPendingIds((s) => new Set(s).add(shiftId));

      // Cancel any refetch so our optimistic write isn't clobbered
      await qc.cancelQueries({ queryKey: ["ops", "payouts"] });

      // Snapshot every cached payouts query (all tabs) for rollback
      const snapshots = qc.getQueriesData<CompletedShiftRow[]>({ queryKey: ["ops", "payouts"] });

      // Optimistically flip paid=true on the matched row across every cached tab.
      // If the current tab is "Due" it'll disappear from view.
      snapshots.forEach(([key, rows]) => {
        if (!rows) return;
        qc.setQueryData<CompletedShiftRow[]>(
          key,
          rows.map((r) => (r.shift_id === shiftId ? { ...r, paid: true } : r)),
        );
      });

      return { snapshots };
    },
    onError: (_err, _shiftId, context) => {
      // Rollback every cached tab to its pre-mutation state
      context?.snapshots.forEach(([key, rows]) => qc.setQueryData(key, rows));
    },
    onSettled: (_data, _err, shiftId) => {
      setPendingIds((s) => {
        const next = new Set(s);
        next.delete(shiftId);
        return next;
      });
      qc.invalidateQueries({ queryKey: ["ops", "payouts"] });
      // The worker sees this in their Earnings tab — flip "Pending" → "Paid".
      qc.invalidateQueries({ queryKey: ["earnings"] });
      qc.invalidateQueries({ queryKey: ["timesheets"] });
    },
  });

  const totalDue = (data ?? [])
    .filter((r) => !r.paid)
    .reduce((sum, r) => sum + parseFloat(r.amount_owed), 0)
    .toFixed(2);

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold tracking-display-tight text-copy">
            Worker payouts
          </h2>
          <div className="relative group">
            <button
              type="button"
              aria-label="How payouts work"
              className="flex h-4 w-4 items-center justify-center rounded-full border border-border-strong text-[10px] font-medium leading-none text-copy-muted hover:border-copy hover:text-copy"
            >
              i
            </button>
            <div className="pointer-events-none invisible absolute left-6 top-0 z-10 w-80 rounded-md border border-border bg-surface-raised p-3 text-xs text-copy opacity-0 shadow-elevated transition-opacity group-hover:visible group-hover:opacity-100">
              <p className="mb-1 font-semibold">How payouts work</p>
              <ul className="space-y-1 text-copy-muted">
                <li>Each completed shift lists the worker OneShift owes money to.</li>
                <li>
                  Send the worker an e-transfer to their phone/email, then click{" "}
                  <strong className="text-copy">Mark paid</strong>.
                </li>
                <li>The worker is notified by email and their earnings view flips to Paid.</li>
                <li>Phase 2: Stripe Connect will automate this.</li>
              </ul>
            </div>
          </div>
        </div>
        <p className="text-xs text-copy-muted">
          Separate from venue invoices (money in). Payouts are money out — OneShift pays the worker.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1.5 rounded-md border border-border bg-surface-sunken p-0.5">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className={cn(
                "whitespace-nowrap rounded-[5px] px-2.5 py-1 text-xs font-medium transition-all",
                tab === t.value
                  ? "bg-surface text-copy shadow-card"
                  : "text-copy-muted hover:text-copy",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === "unpaid" && parseFloat(totalDue) > 0 && (
          <div className="text-sm">
            <span className="text-copy-muted">Total due: </span>
            <span className="font-semibold tabular-nums text-copy">${totalDue}</span>
          </div>
        )}
      </div>

      {isLoading && <div className="text-sm text-copy-muted">Loading…</div>}
      {error && <div className="text-sm text-destructive">Could not load payouts.</div>}

      {data && data.length === 0 && (
        <div className="rounded-lg border border-border bg-surface p-10 text-center text-sm text-copy-muted shadow-card">
          {tab === "unpaid"
            ? "No workers awaiting payment."
            : tab === "paid"
              ? "No payments processed yet."
              : "No completed shifts yet."}
        </div>
      )}

      {data && data.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border bg-surface shadow-card">
         <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="border-b border-border bg-surface-sunken text-left text-xs uppercase tracking-wider text-copy-muted">
              <tr>
                <th className="px-4 py-3 font-medium">Worker</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Shift</th>
                <th className="px-4 py-3 text-right font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((row) => (
                <tr
                  key={row.shift_id}
                  className="transition-colors hover:bg-surface-sunken/40"
                >
                  <td className="px-4 py-3 font-medium text-copy">{row.worker_name}</td>
                  <td className="px-4 py-3 text-xs text-copy-muted">
                    <div>{row.worker_phone ?? "—"}</div>
                    <div>{row.worker_email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="text-copy">
                      {ROLE_LABELS[row.role]} · {row.venue_name}
                    </div>
                    <div className="text-copy-muted">
                      {new Date(row.date).toLocaleDateString()} ·{" "}
                      {formatTimeRange12h(row.start_time, row.end_time)} · {row.hours}h
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-copy">
                    ${row.amount_owed}
                  </td>
                  <td className="px-4 py-3">
                    {row.paid ? (
                      <Badge variant="success" dot>
                        Paid
                      </Badge>
                    ) : (
                      <Badge variant="warning" dot>
                        Due
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {!row.paid ? (
                        <Button
                          size="sm"
                          onClick={() => pay.mutate(row.shift_id)}
                          disabled={pendingIds.has(row.shift_id)}
                        >
                          {pendingIds.has(row.shift_id) ? "Paying…" : "Mark paid"}
                        </Button>
                      ) : (
                        <span className="text-xs text-copy-subtle">—</span>
                      )}
                      <Button
                        variant="outline"
                        size="icon-sm"
                        aria-label="Delete payout entry"
                        onClick={() => setConfirmDelete(row)}
                        className="text-destructive-foreground hover:bg-destructive-light"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
         </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete !== null}
        destructive
        title="Delete this payout entry?"
        description={
          <>
            This permanently deletes the completed shift for{" "}
            <span className="font-medium text-copy">{confirmDelete?.worker_name}</span>{" "}
            at {confirmDelete?.venue_name} (and its check-in/out, rating, and
            timesheet records). The worker and venue are unaffected. This cannot
            be undone.
          </>
        }
        confirmLabel="Delete permanently"
        loadingLabel="Deleting…"
        loading={remove.isPending}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.shift_id)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
